import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Calendar,
  CalendarDays,
  CalendarRange,
  Check,
  Clock,
  Gift,
  Layers,
  Sparkles,
  Unlock,
} from 'lucide-react';
import { Button } from '../shared/Button';
import { SectionHeading } from '../shared/SectionHeading';
import { PassCheckoutDialog } from '../payments/PassCheckoutDialog';
import { usePassCheckout } from '../../hooks/usePassCheckout';
import { useAuth } from '../../contexts/AuthContext';
import { formatCurrency } from '../../utils/currency';
import {
  FREE_TIER_I18N_KEY,
  PASS_DURATION_LABEL_KEYS,
  PASS_TIERS,
  SUBSCRIPTION_PLANS,
  isPassesModel,
} from '../../config/pricing';

interface PricingCardModel {
  key: string;
  badgeIcon: React.ElementType;
  badgeColor: string;
  variant: 'primary' | 'secondary';
  highlighted: boolean;
  price: number;
  isFree: boolean;
  suffix?: string;
  vat?: string;
  name: string;
  badge: string;
  description: string;
  features: string[];
  cta: string;
  onClick: () => void;
  footerLink?: { text: string; linkText: string; href: string };
}

export const PricingCards: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { requestCheckout, isDialogOpen, isSubmitting, error, submitEmail, closeDialog } =
    usePassCheckout();

  const subscriptionVisuals: Record<
    string,
    { badgeIcon: React.ElementType; badgeColor: string; variant: 'primary' | 'secondary' }
  > = {
    free: {
      badgeIcon: Gift,
      badgeColor: 'bg-gray-100 text-gray-700',
      variant: 'secondary',
    },
    level_bundle: {
      badgeIcon: Layers,
      badgeColor: 'bg-secondary text-white',
      variant: 'primary',
    },
    full_access: {
      badgeIcon: Unlock,
      badgeColor: 'bg-gradient-to-r from-primary to-secondary text-white',
      variant: 'primary',
    },
  };

  const passVisuals: Record<string, { badgeIcon: React.ElementType; badgeColor: string }> = {
    pass_24h: { badgeIcon: Clock, badgeColor: 'bg-primary/10 text-primary' },
    pass_1_week: { badgeIcon: Calendar, badgeColor: 'bg-primary/10 text-primary' },
    pass_2_weeks: { badgeIcon: CalendarDays, badgeColor: 'bg-primary/10 text-primary' },
    pass_1_month: {
      badgeIcon: CalendarRange,
      badgeColor: 'bg-gradient-to-r from-primary to-secondary text-white',
    },
  };

  const freeCard: PricingCardModel = {
    key: 'free',
    badgeIcon: Gift,
    badgeColor: 'bg-gray-100 text-gray-700',
    variant: 'secondary',
    highlighted: false,
    price: 0,
    isFree: true,
    name: t(`${FREE_TIER_I18N_KEY}.name`),
    badge: t(`${FREE_TIER_I18N_KEY}.badge`),
    description: t(`${FREE_TIER_I18N_KEY}.description`),
    features: t(`${FREE_TIER_I18N_KEY}.features`, { returnObjects: true }) as string[],
    cta: t(`${FREE_TIER_I18N_KEY}.cta`),
    onClick: () => navigate(isAuthenticated ? '/dashboard' : '/try-free'),
  };

  const subscriptionCards: PricingCardModel[] = SUBSCRIPTION_PLANS.map((plan) => {
    const visuals = subscriptionVisuals[plan.id];
    const isFree = plan.price === 0;
    return {
      key: plan.id,
      badgeIcon: visuals.badgeIcon,
      badgeColor: visuals.badgeColor,
      variant: visuals.variant,
      highlighted: plan.recommended,
      price: plan.price,
      isFree,
      suffix: isFree ? undefined : t('landing.pricing.perMonth'),
      vat: isFree ? undefined : t('landing.pricing.vatIncluded'),
      name: t(`${plan.i18nKey}.name`),
      badge: t(`${plan.i18nKey}.badge`),
      description: t(`${plan.i18nKey}.description`),
      features: t(`${plan.i18nKey}.features`, { returnObjects: true }) as string[],
      cta: t(`${plan.i18nKey}.cta`),
      onClick: () => {
        if (isFree) {
          navigate(isAuthenticated ? '/dashboard' : '/try-free');
        } else {
          navigate(`/checkout?plan=${plan.id}`);
        }
      },
    };
  });

  const passCards: PricingCardModel[] = PASS_TIERS.map((tier) => {
    const visuals = passVisuals[tier.id];
    return {
      key: tier.id,
      badgeIcon: visuals.badgeIcon,
      badgeColor: visuals.badgeColor,
      variant: 'primary',
      highlighted: tier.recommended,
      price: tier.price,
      isFree: false,
      suffix: t('landing.pricing.passes.oneTime'),
      vat: t('landing.pricing.passes.vatIncluded'),
      name: t(`${tier.i18nKey}.name`),
      badge: t(PASS_DURATION_LABEL_KEYS[tier.duration]),
      description: t(`${tier.i18nKey}.description`),
      features: t(`${tier.i18nKey}.features`, { returnObjects: true }) as string[],
      cta: t('landing.pricing.passes.buyCta'),
      onClick: () => requestCheckout(tier.id),
      footerLink: isAuthenticated
        ? {
            text: t('landing.pricing.passes.signedInPrompt'),
            linkText: t('landing.pricing.passes.dashboardLink'),
            href: '/dashboard',
          }
        : {
            text: t('landing.pricing.passes.haveAccount'),
            linkText: t('landing.pricing.passes.loginLink'),
            href: '/login?redirect=/pricing',
          },
    };
  });

  const showingPasses = isPassesModel;
  const cards = showingPasses ? [freeCard, ...passCards] : subscriptionCards;
  const gridClassName = showingPasses
    ? 'mt-12 grid grid-cols-1 items-stretch gap-6 md:grid-cols-2 xl:grid-cols-5'
    : 'mt-12 grid grid-cols-1 items-stretch gap-6 md:grid-cols-3';

  const eyebrowKey = showingPasses ? 'landing.pricing.passes.eyebrow' : 'landing.pricing.eyebrow';
  const titleKey = showingPasses ? 'landing.pricing.passes.title' : 'landing.pricing.title';
  const titleHighlightKey = showingPasses
    ? 'landing.pricing.passes.titleHighlight'
    : 'landing.pricing.titleHighlight';
  const subtitleKey = showingPasses ? 'landing.pricing.passes.subtitle' : 'landing.pricing.subtitle';
  const recommendedLabelKey = showingPasses
    ? 'landing.pricing.passes.recommended'
    : 'landing.pricing.recommended';
  const guarantees = showingPasses
    ? [
        t('landing.pricing.passes.guarantees.noHidden'),
        t('landing.pricing.passes.guarantees.oneTime'),
      ]
    : [
        t('landing.pricing.guarantees.noHidden'),
        t('landing.pricing.guarantees.cancelAnytime'),
      ];
  const faqLinkKey = showingPasses
    ? 'landing.pricing.passes.faqLink'
    : 'landing.pricing.faqLink';

  return (
    <section id="pricing" className="section-shell-muted">
      <div className="section-container">
        <SectionHeading
          eyebrow={t(eyebrowKey)}
          title={
            showingPasses
              ? t(titleKey)
              : `${t(titleKey).replace(t(titleHighlightKey), '')} ${t(titleHighlightKey)}`
          }
          subtitle={t(subtitleKey)}
        />

        <div className={gridClassName}>
          {cards.map((card, index) => {
            const BadgeIcon = card.badgeIcon;
            return (
              <div
                key={index}
                className={`relative flex flex-col rounded-3xl border p-7 shadow-surface transition-all duration-300 ${
                  card.highlighted
                    ? 'border-secondary/70 bg-white ring-2 ring-secondary/30'
                    : 'border-primary/10 bg-white/90'
                }`}
              >
                {card.highlighted && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <div className="flex items-center justify-center gap-1 rounded-full bg-secondary px-4 py-1 text-center text-xs font-semibold uppercase tracking-wide text-white">
                      {showingPasses ? <Sparkles className="w-4 h-4" /> : <Layers className="w-4 h-4" />}
                      {t(recommendedLabelKey)}
                    </div>
                  </div>
                )}

                <div className={`${card.badgeColor} mb-5 inline-flex items-center justify-center gap-2 rounded-full px-3 py-1 text-center text-xs font-semibold uppercase tracking-wide`}>
                  <BadgeIcon className="w-4 h-4" />
                  {card.badge}
                </div>

                <h3 className="text-2xl font-bold text-ink">{card.name}</h3>

                <div className="mb-3 mt-4">
                  <span className="text-4xl font-bold text-primary">
                    {card.isFree ? t('landing.pricing.freePrice') : formatCurrency(card.price, i18n.language)}
                  </span>
                  {card.suffix && (
                    <span className="ml-1 text-slate-600">{card.suffix}</span>
                  )}
                  {card.vat && <div className="mt-0.5 text-xs text-slate-500">{card.vat}</div>}
                </div>

                <p className="mb-6 text-sm text-slate-600">{card.description}</p>

                <ul className="space-y-4 mb-8">
                  {card.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <div className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-success/10">
                        <Check className="w-3 h-3 text-success" />
                      </div>
                      <span className="text-sm text-slate-700">{feature}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-auto">
                  <Button
                    variant={card.variant}
                    className={`w-full ${card.highlighted ? 'bg-secondary shadow-xl-secondary hover:brightness-105' : ''}`}
                    size="pricing"
                    onClick={card.onClick}
                  >
                    {card.cta}
                  </Button>

                  {card.footerLink && (
                    <p className="mt-3 text-center text-sm text-slate-500">
                      {card.footerLink.text}{' '}
                      <Link to={card.footerLink.href} className="text-primary underline hover:no-underline">
                        {card.footerLink.linkText}
                      </Link>
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {error && !isDialogOpen && (
          <p className="mt-6 rounded-2xl border border-error/25 bg-error/10 p-4 text-center text-sm text-error">
            {error}
          </p>
        )}

        <div className="mt-10 space-y-4 text-center">
          <div className="flex flex-wrap justify-center gap-6 text-sm text-slate-600">
            {guarantees.map((guarantee, index) => (
              <span key={index} className="flex items-center gap-2">
                <Check className="w-4 h-4 text-success" />
                {guarantee}
              </span>
            ))}
          </div>
          <p className="text-sm text-slate-500">
            <a href="#faq" className="text-primary underline hover:no-underline">
              {t(faqLinkKey)}
            </a>
          </p>
        </div>
      </div>

      <PassCheckoutDialog
        open={isDialogOpen}
        submitting={isSubmitting}
        error={error}
        onSubmit={submitEmail}
        onClose={closeDialog}
      />
    </section>
  );
};

export default PricingCards;
